import React from "react";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { WireGuardTunnel } from "../wireGuardTunnelsInterfaces";
import {
  MULISH,
  PANEL_BORDER,
  STATUS_STYLES,
  STEP_ORDER,
  TARGET_PANEL_WIDTH_PX,
} from "./vpnDebugConstants";
import { VpnDebugClassificationBanner } from "./VpnDebugClassificationBanner";
import { VpnDebugStepCard } from "./VpnDebugStepCard";
import { VpnDebugTargetForm } from "./VpnDebugTargetForm";
import { fetchVpnDebugTargets } from "./VpnDebuggerUtils/fetchVpnDebugTargets";
import { runVpnDebugLadder } from "./VpnDebuggerUtils/runVpnDebugLadder";
import { runVpnFlowWindow } from "./VpnDebuggerUtils/runVpnFlowWindow";
import { FLOW_WINDOW_SECONDS } from "./vpnDebugConstants";
import { useVpnDebuggerStore } from "./vpnDebuggerStore/vpnDebuggerStore";
import { VpnDebugStepStatus } from "./vpnDebugInterfaces";
import { SHADED_SURFACE_BG } from "../../Troubleshooting/TroubleshootingUi";

interface Props {
  /** Already loaded by the page — the debugger issues no tunnel fetch of its own. */
  tunnels: WireGuardTunnel[];
  getServerName: (serverId: number | null) => string;
}

const COUNT_ORDER: VpnDebugStepStatus[] = ["pass", "fail", "warn", "skipped"];

/**
 * VPN Debugger tab.
 *
 * Runs a fixed ladder of checks from the tunnel's WireGuard gateway — the only
 * host that can route into the customer's network — and renders each step with a
 * diagnosis and a next action, then one classification of the fault.
 *
 * Laid out as ladder + target panel to match the Tunnels tab's table + filters
 * panel, so the two tabs read as one page and the form stays on screen while an
 * operator reads a result.
 */
export const VpnDebuggerPanel = ({ tunnels, getServerName }: Props) => {
  const navigate = useNavigate();
  const {
    tunnelId,
    targetsError,
    setTargets,
    setTargetsLoading,
    setTargetsError,
    setSelectedCameraId,
    steps,
    pendingSteps,
    classification,
    customerStepIds,
    running,
    stage,
    statusMessage,
    statusIsError,
    flowOffered,
    flowTunnelTaskId,
    reset,
  } = useVpnDebuggerStore();

  // Start each visit from a clean slate (no stale ladder or target).
  React.useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Camera candidates for the picker. Synchronous server-side — no SSM, so this
  // is cheap enough to refetch on every tunnel change.
  React.useEffect(() => {
    if (tunnelId == null) {
      setTargets(null);
      setTargetsError("");
      return;
    }
    let cancelled = false;
    setSelectedCameraId(null);
    setTargetsLoading(true);
    setTargetsError("");
    fetchVpnDebugTargets(tunnelId)
      .then((data) => {
        if (cancelled) return;
        setTargets(data);
      })
      .catch(() => {
        if (cancelled) return;
        setTargets(null);
        setTargetsError("Failed to load cameras for this tunnel.");
      })
      .finally(() => {
        if (!cancelled) setTargetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tunnelId]);

  // Evidence auto-expands on the first failing step only: that is the one thing
  // the operator opened this tab for.
  const firstFailingStepId = STEP_ORDER.find(
    (id) => steps[id]?.status === "fail"
  );

  const counts = React.useMemo(() => {
    const tally: Partial<Record<VpnDebugStepStatus, number>> = {};
    STEP_ORDER.forEach((id) => {
      const status = steps[id]?.status;
      if (status) tally[status] = (tally[status] || 0) + 1;
    });
    return tally;
  }, [steps]);

  const hasResults = Object.keys(counts).length > 0;

  // null = operator, or no result yet: draw the whole ladder.
  const visibleStepIds = customerStepIds
    ? STEP_ORDER.filter((id) => customerStepIds.includes(id))
    : STEP_ORDER;

  return (
    <Stack
      direction="row"
      gap={2}
      alignItems="stretch"
      sx={{ height: "100%", minHeight: 0, minWidth: 900, overflowX: "auto" }}
    >
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          flexShrink: 0,
          width: TARGET_PANEL_WIDTH_PX,
        }}
      >
        <VpnDebugTargetForm
          tunnels={tunnels}
          getServerName={getServerName}
          onRun={() => {
            void runVpnDebugLadder({ navigate });
          }}
        />
      </Box>
      <Paper
        sx={{
          flex: 1,
          minWidth: 520,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 2,
          borderTop: PANEL_BORDER,
          backgroundColor: SHADED_SURFACE_BG,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            flexShrink: 0,
            borderBottom: PANEL_BORDER,
            backgroundColor: SHADED_SURFACE_BG,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            minHeight: 46,
          }}
        >
          <Typography sx={{ ...MULISH, fontWeight: 700, fontSize: 14 }}>
            DIAGNOSTIC LADDER
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {hasResults &&
            COUNT_ORDER.filter((status) => counts[status]).map((status) => {
              const style = STATUS_STYLES[status];
              return (
                <Box
                  key={status}
                  component="span"
                  sx={{
                    ...MULISH,
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: style.color,
                    backgroundColor: style.backgroundColor,
                    borderRadius: "999px",
                    px: 1,
                    py: "2px",
                  }}
                >
                  {counts[status]} {style.label.toLowerCase()}
                </Box>
              );
            })}
        </Box>

        <Box sx={{ px: 2.5, py: 2, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {targetsError && (
            <Alert severity="warning" sx={{ ...MULISH, mb: 2 }}>
              {targetsError}
            </Alert>
          )}

          {statusMessage && statusIsError && (
            <Alert severity="error" sx={{ ...MULISH, mb: 2 }}>
              {statusMessage}
            </Alert>
          )}

          <VpnDebugClassificationBanner
            running={running}
            stage={stage}
            steps={steps}
            pendingSteps={pendingSteps}
            classification={classification}
          />

          {!running && !hasResults && !statusIsError && (
            <Typography
              sx={{ ...MULISH, fontSize: 13, color: "#6b7280", mb: 2.5 }}
            >
              Pick a tunnel and a camera, then run the ladder. Every check runs
              from the tunnel&apos;s WireGuard gateway — the only host that can
              route into the site&apos;s network — and reports a diagnosis
              and a next action.
            </Typography>
          )}

          {statusMessage && !statusIsError && !running && (
            <Typography
              sx={{ ...MULISH, fontSize: 13, color: "#6b7280", mb: 2.5 }}
            >
              {statusMessage}
            </Typography>
          )}

          {/* On a stale handshake the next question is always the same one:
              are their packets reaching us at all? Until the arrival counters
              landed that meant hand-running tcpdump over SSM. Offered rather
              than run automatically -- it costs a real 30s, and the useful way
              to spend it is while the customer restarts their tunnel. */}
          {flowOffered && !running && tunnelId != null && (
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{ ...MULISH, fontSize: 13, color: "#6b7280", mb: 1 }}
              >
                The handshake is stale. Watch the gateway to see whether packets
                from the site arrive at all.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                data-testid="watch-packet-flow-button"
                onClick={() => {
                  void runVpnFlowWindow(
                    tunnelId,
                    flowTunnelTaskId,
                    navigate,
                    () => {
                      void runVpnFlowWindow(
                        tunnelId,
                        flowTunnelTaskId,
                        navigate,
                        () => undefined
                      );
                    }
                  );
                }}
                sx={{ ...MULISH, textTransform: "none" }}
              >
                {`Watch packet flow for ${FLOW_WINDOW_SECONDS}s`}
              </Button>
            </Box>
          )}

          {/* Every step this audience is shown is rendered, always. A missing
              row reads as a bug, and "this check never ran" is itself
              diagnostic. For a customer that is the server's allowlist: a step
              the projection withholds (tunnel_config) would otherwise sit at
              "Waiting" for ever. */}
          {visibleStepIds.map((stepId, index) => (
            <VpnDebugStepCard
              key={stepId}
              stepId={stepId}
              step={steps[stepId]}
              isPending={running && pendingSteps.includes(stepId)}
              autoExpandEvidence={stepId === firstFailingStepId}
              isLast={index === visibleStepIds.length - 1}
            />
          ))}
        </Box>
      </Paper>
    </Stack>
  );
};
